import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ShoppingCartIcon, 
  UserIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  HeartIcon,
  PlusIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

import { 
  selectIsAuthenticated, 
  selectUser, 
  logout 
} from '../../store/slices/authSlice';
import { 
  selectCartItemCount, 
  openCart 
} from '../../store/slices/cartSlice';
import { selectWatchlistItemCount } from '../../store/slices/watchlistSlice';
import SearchBar from '../Common/SearchBar';
import { usePermissions } from '../../contexts/PermissionContext';
import WarehouseSelector from '../WarehouseSelector';
import DisclaimerModal from '../Common/DisclaimerModal';
import Portal from '../Common/Portal';
import AddToHomeScreen from '../Common/AddToHomeScreen';

const Header = () => {
  const menuButtonRef = React.useRef(null);
  const [menuPosition, setMenuPosition] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [tabletSearchOpen, setTabletSearchOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyAdminAccess, canAccessAdminDashboard, permissions } = usePermissions();
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const userDisplayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.email || 'Account';
  
  // Force debug output of authentication state
  console.debug('[Header] Auth:', { isAuthenticated, role: user?.role, canAccessAdmin: canAccessAdminDashboard() });
  const cartItemCount = useSelector(selectCartItemCount);
  const watchlistCount = useSelector(selectWatchlistItemCount);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const disclaimerKey = 'vehicle_disclaimer_ack';
  const hasAckedDisclaimer = () => {
    try { return sessionStorage.getItem(disclaimerKey) === '1'; } catch { return false; }
  };
  const setAckedDisclaimer = () => {
    try { sessionStorage.setItem(disclaimerKey, '1'); } catch {}
  };

  const handleFindClick = (e, opts = {}) => {
    if (hasAckedDisclaimer()) return; // allow normal navigation
    e.preventDefault();
    e.stopPropagation();
    setDisclaimerOpen(true);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return;
    }
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobileView(event.matches);
    setIsMobileView(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Close More dropdown when clicking outside
  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen]);

  // Close tablet search when clicking outside
  const tabletSearchRef = React.useRef(null);
  useEffect(() => {
    if (!tabletSearchOpen) return;
    const handleClickOutside = (e) => {
      if (tabletSearchRef.current && !tabletSearchRef.current.contains(e.target)) {
        setTabletSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tabletSearchOpen]);

  const handleDisclaimerAgree = () => {
    setAckedDisclaimer();
    setDisclaimerOpen(false);
    navigate(useMobileRoutes ? '/mobile/makes' : '/makes');
  };
  const handleDisclaimerClose = () => setDisclaimerOpen(false);

  const isMobileNamespace = (location?.pathname || '').startsWith('/mobile');
  const useMobileRoutes = isMobileNamespace || isMobileView;
  const homeHref = useMobileRoutes ? '/mobile' : '/';
  const accountHref = useMobileRoutes ? '/mobile/accounts' : '/account';
  const loginHref = useMobileRoutes ? '/mobile/login' : '/login';
  const registerHref = useMobileRoutes ? '/mobile/register' : '/register';
  const watchlistHref = useMobileRoutes ? '/mobile/watchlist' : '/watchlist';

  // Derived, robust role checks (case-insensitive)
  const rawRole = (user?.role || user?.role_name || user?.Role?.name || '').toString();
  const role = rawRole.trim();
  const roleLower = role.toLowerCase();
  const isAdminRole = roleLower === 'admin' || roleLower === 'administrator';
  const isManagerRole = roleLower === 'manager';
  const isStaffRole = ['admin', 'administrator', 'manager', 'staff'].includes(roleLower);
  // Show icon only for built-in staff/admin roles — custom/customer roles never see the gear
  const showAdminEntry = !!(isAuthenticated && isStaffRole);

  // DEBUG: trace admin menu conditions
  try {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[Header] auth:', isAuthenticated, 'role:', role);
      console.debug('[Header] roleLower:', roleLower, 'isAdminRole:', isAdminRole, 'isManagerRole:', isManagerRole);
      console.debug('[Header] hasAnyAdminAccess():', hasAnyAdminAccess(), 'canAccessAdminDashboard():', canAccessAdminDashboard());
      console.debug('[Header] showAdminEntry:', showAdminEntry);
    }
  } catch {}

  // Additional debug when permissions change
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('[Header] Permissions update:', {
      role: user?.role,
      isAuthenticated,
      canAccessAdmin: canAccessAdminDashboard(),
      hasAnyAdminAccess: hasAnyAdminAccess(),
      dashboardPerm: permissions?.dashboard
    });
  }, [isAuthenticated, user, permissions]);

  const handleLogout = () => {
    dispatch(logout());
    navigate(homeHref);
  };

  const handleCartClick = () => {
    dispatch(openCart());
  };

  const navigation = [
    { name: 'Find Your Vehicle', href: useMobileRoutes ? '/mobile/makes' : '/makes', requiresDisclaimer: true },
    { name: 'Auto Glass Finder', href: useMobileRoutes ? '/mobile/parts-finder' : '/parts-finder' },
    { name: 'VIN Search', href: useMobileRoutes ? '/mobile/vin-search' : '/autoglass-vin-search' },
    { name: 'Accessories', href: useMobileRoutes ? '/mobile/accessories-supplies' : '/accessories-supplies', overflow: true },
    ...(isAuthenticated
      ? [{ name: 'Quick Entry', href: useMobileRoutes ? '/mobile/quick-entry' : '/quick-entry', overflow: true }]
      : []),
    { name: 'Contact', href: useMobileRoutes ? '/mobile/contact' : '/contact', overflow: true },
    ...(useMobileRoutes
      ? [{ name: 'Watchlist', href: '/mobile/watchlist' }]
      : []),
  ];
  const primaryNav = navigation.filter((i) => !i.overflow);
  const overflowNav = navigation.filter((i) => i.overflow);

  const iconNavigation = useMobileRoutes
    ? []
    : [
        {
          name: 'Watchlist',
          href: '/watchlist',
          icon: HeartIcon,
          title: 'View Watchlist',
        },
        {
          name: 'Wish',
          href: '/wish',
          title: 'View Wish List',
        },
      ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-[120]">
      <nav className="max-w-8xl mx-auto px-2 sm:px-2 lg:px-6">
        <div className="md:hidden py-2 flex items-center gap-2 relative z-[170]">
          <Link to={homeHref} className="flex items-center">
            <picture>
              <source srcSet="/images/AGS_logo.webp" type="image/webp" />
              <img
                src="/images/AGS_logo.png"
                alt="Auto Glass Shop"
                width="96"
                height="40"
                decoding="async"
                className="h-10 w-auto object-contain"
              />
            </picture>
          </Link>
          <button
            className="p-2 text-gray-500 hover:text-gray-700"
            onClick={() => setMobileSearchOpen((v) => !v)}
            aria-label="Open search"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-[90px] max-w-[150px]">
            <WarehouseSelector size="sm" />
          </div>
          <Link
            to={watchlistHref}
            className="relative p-2 text-gray-500 hover:text-red-500"
            aria-label="Watchlist"
          >
            <HeartIcon className="h-5 w-5" />
            {watchlistCount > 0 && (
              <span className="absolute top-1.5 right-1 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                {Math.min(watchlistCount, 99)}
              </span>
            )}
          </Link>
          {isAuthenticated ? (
            <Link
              to={accountHref}
              className="text-sm font-semibold text-gray-700 truncate max-w-[80px]"
            >
              {userDisplayName}
            </Link>
          ) : (
            <Link to={loginHref} className="text-sm font-semibold text-primary-600">
              Sign In
            </Link>
          )}
          <button
            className="p-2 text-gray-600 hover:text-gray-800"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>

        <div className="hidden md:flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 pr-1 md:pr-2 lg:pr-4">
            <Link to={homeHref} className="flex items-center">
              <picture>
                <source srcSet="/images/AGS_logo.webp" type="image/webp" />
                <img
                  src="/images/AGS_logo.png"
                  alt="Auto Glass Shop"
                  width="96"
                  height="56"
                  decoding="async"
                  fetchpriority="high"
                  className="h-14 w-auto object-contain hover:opacity-90 transition-opacity duration-200"
                />
              </picture>
              <span className="hidden">In Quality We Trust</span>
            </Link>
          </div>


          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center ml-2 lg:ml-4 pr-2 flex-nowrap flex-shrink flex-grow min-w-0 space-x-1 md:space-x-1 lg:space-x-2 xl:space-x-4">
            {/* Primary nav items — always visible */}
            {primaryNav.map((item) => (
              item.requiresDisclaimer ? (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleFindClick}
                  className="text-gray-700 hover:text-primary-600 px-1 lg:px-2 py-2 text-xs lg:text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-primary-600 px-1 lg:px-2 py-2 text-xs lg:text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  {item.name}
                </Link>
              )
            ))}
            {/* Overflow nav: inline at xl, dropdown at md/lg */}
            {overflowNav.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="hidden lg:block text-gray-700 hover:text-primary-600 px-1 lg:px-2 py-2 text-xs lg:text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              >
                {item.name}
              </Link>
            ))}
            {/* More dropdown — visible at md/lg only, hidden at xl */}
            {overflowNav.length > 0 && (
              <div className="lg:hidden relative" ref={moreMenuRef}>
                <button
                  onClick={() => setMoreMenuOpen((v) => !v)}
                  className="flex items-center gap-0.5 text-gray-700 hover:text-primary-600 px-1 lg:px-2 py-2 text-xs lg:text-sm font-medium whitespace-nowrap"
                >
                  More <ChevronDownIcon className="h-3 w-3 mt-0.5" />
                </button>
                {moreMenuOpen && (
                  <div className="absolute left-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[200]">
                    {overflowNav.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMoreMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2 xl:space-x-4 flex-shrink-0 flex-nowrap">
            {/* Desktop Search - full input only on xl+; icon only at lg */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const query = e.currentTarget.elements.search.value.trim();
                if (query) {
                  navigate(`/products?search=${encodeURIComponent(query)}`);
                }
              }}
              className="relative hidden xl:block"
            >
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                name="search"
                type="text"
                placeholder="Search for auto glass by SKU or keywords"
                className="block w-44 xl:w-56 2xl:w-96 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900"
              />
            </form>

            {/* Tablet/laptop Search Dropdown (xl:hidden) */}
            <div ref={tabletSearchRef} className="xl:hidden relative">
              <button
                className="p-2 text-gray-400 hover:text-gray-600"
                onClick={() => setTabletSearchOpen((v) => !v)}
                aria-label="Open search"
                title="Search"
              >
                <MagnifyingGlassIcon className="h-6 w-6" />
              </button>
              {tabletSearchOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-[200]">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const query = e.currentTarget.elements.search.value.trim();
                      if (query) {
                        setTabletSearchOpen(false);
                        navigate(`/products?search=${encodeURIComponent(query)}`);
                      }
                    }}
                  >
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        name="search"
                        type="text"
                        placeholder="Search SKU or keywords..."
                        autoFocus
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Warehouse Selector - header shows on desktop only; mobile shows it inside the sliding panel */}
            <div className="hidden md:block">
              <WarehouseSelector />
            </div>

            {/* WhatsApp quick chat removed from header per request; bottom floating button remains */}

            {/* Admin quick access icon — visible on all viewports when permitted */}
            {showAdminEntry && (
              <Link
                to="/admin"
                className="p-2 text-gray-600 hover:text-primary-600 transition-colors duration-200"
                title="Admin Dashboard"
                aria-label="Admin Dashboard"
              >
                <Cog6ToothIcon className="h-6 w-6" />
              </Link>
            )}

            {/* Watchlist heart icon */}
            <Link
              to={watchlistHref}
              className="relative p-1.5 text-gray-400 hover:text-red-500 transition-colors duration-200"
              title="Watchlist"
              aria-label="Watchlist"
            >
              <HeartIcon className="h-5 w-5" />
              {watchlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                  {Math.min(watchlistCount, 99)}
                </span>
              )}
            </Link>

            {/* Cart */}
            <button
              onClick={handleCartClick}
              className="relative p-1.5 text-gray-400 hover:text-gray-500 transition-colors duration-200"
            >
              <ShoppingCartIcon className="h-6 w-6" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center font-medium">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="hidden md:block">
                <Menu as="div" className="relative">
                  {({ open }) => (
                    <>
                      <Menu.Button 
                        ref={menuButtonRef}
                        className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md"
                        onClick={() => {
                          setMobileSearchOpen(false);
                          setMobileMenuOpen(false);
                          if (menuButtonRef.current) {
                            const rect = menuButtonRef.current.getBoundingClientRect();
                            setMenuPosition({ 
                              top: rect.bottom + window.scrollY + 8,
                              right: window.innerWidth - rect.right
                            });
                          }
                        }}
                      >
                        <UserIcon className="h-6 w-6" />
                        <div className="hidden sm:block text-left">
                          <div className="text-sm font-medium">
                            {user?.first_name || 'Account'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user?.email}
                          </div>
                        </div>
                        <ChevronDownIcon className="h-4 w-4" />
                      </Menu.Button>
  
                      <Portal>
                        <Transition
                          show={open}
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items 
                            static 
                            className="fixed w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[100]"
                            style={menuPosition ? { top: `${menuPosition.top}px`, right: `${menuPosition.right}px` } : {}}
                          >
                            <div className="py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to={useMobileRoutes ? '/mobile/accounts' : '/account'}
                                    {...(useMobileRoutes ? { state: { initialTab: 'account' } } : {})}
                                    className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                                  >
                                    My Account
                                  </Link>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to={useMobileRoutes ? '/mobile/accounts' : '/orders'}
                                    {...(useMobileRoutes ? { state: { initialTab: 'orders' } } : {})}
                                    className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                                  >
                                    My Orders
                                  </Link>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to={useMobileRoutes ? '/mobile/accounts' : '/quote-requests'}
                                    {...(useMobileRoutes ? { state: { initialTab: 'quotes' } } : {})}
                                    className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                                  >
                                    My Quote Requests
                                  </Link>
                                )}
                              </Menu.Item>
                              {isAuthenticated && canAccessAdminDashboard() && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <Link
                                      to="/admin"
                                      className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                                    >
                                      Admin Panel
                                    </Link>
                                  )}
                                </Menu.Item>
                              )}
                              <hr className="my-1" />
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={handleLogout}
                                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                                  >
                                    Sign Out
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Portal>
                    </>
                  )}
                </Menu>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                {/* Mobile: Sign In button */}
                <Link to={loginHref} className="md:hidden btn-primary text-sm">Sign In</Link>
                {/* Desktop: Sign In link */}
                <Link to={loginHref} className="hidden md:inline text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200">Sign In</Link>
                {/* Desktop: Sign Up button */}
                <Link to={registerHref} className="hidden md:inline btn-primary text-sm">Sign Up</Link>
              </div>
            )}
          </div>
        </div>

        {/* Second Row removed: search now uses overlay triggered by icon */}

        <Transition show={mobileSearchOpen} as={Fragment}>
          <div className="md:hidden">
            <Transition
              appear
              show={mobileSearchOpen}
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 -translate-y-6"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 -translate-y-6"
            >
              <div className="relative bg-white shadow-md border-b overflow-x-hidden">
                <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium text-gray-600 hidden md:block">Search</div>
                    <button
                      className="ml-auto p-2 text-gray-400 hover:text-gray-600"
                      onClick={() => setMobileSearchOpen(false)}
                      aria-label="Close search"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <SearchBar autoFocus />
                </div>
              </div>
            </Transition>
          </div>
        </Transition>

        {/* Mobile menu: sliding side panel from the right */}
        <Transition show={mobileMenuOpen}>
          <div className="md:hidden fixed inset-0 z-[180]">
            {/* Backdrop fade */}
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="absolute inset-0 bg-black/30"
                onClick={() => setMobileMenuOpen(false)}
                aria-hidden="true"
              />
            </Transition.Child>
            {/* Sliding panel container */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-y-0 right-0 flex max-w-full">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <div className="pointer-events-auto w-[80vw] sm:w-[80vw] lg:max-w-sm bg-[#E8F5E9] shadow-xl h-full flex flex-col">
                    {/* Panel header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#738f52]/20 bg-[#E8F5E9]">
                      <div className="text-base font-semibold text-[#738f52]">Menu</div>
                      <button
                        className="p-2 text-[#738f52] hover:text-[#5a7242]"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Close menu"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    {/* Panel content */}
                    <div className="flex-1 overflow-y-auto overflow-x-visible px-3 py-4 space-y-4">
                      {/* Branch selector in panel */}
                      <div className="overflow-visible">
                        <WarehouseSelector size="sm" />
                      </div>

                      <hr className="border-[#738f52]/20" />

                      {/* Navigation */}
                      <nav className="space-y-1">
                        {navigation.map((item) => (
                          item.requiresDisclaimer ? (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#738f52] hover:bg-[#738f52]/10 rounded-md"
                              onClick={(e) => { handleFindClick(e); setMobileMenuOpen(false); }}
                            >
                              {item.name}
                            </Link>
                          ) : (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#738f52] hover:bg-[#738f52]/10 rounded-md"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.name}
                            </Link>
                          )
                        ))}
                      </nav>

                      {/* Icon Navigation */}
                      <div className="space-y-1">
                        {iconNavigation.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:text-[#738f52] hover:bg-[#738f52]/10 rounded-md"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.icon && <item.icon className="h-5 w-5 mr-3" />}
                            {item.name}
                          </Link>
                        ))}
                      </div>

                      {/* Admin link for staff (include manager) */}
                      {showAdminEntry && (
                        <Link
                          to="/admin"
                          className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#738f52] hover:bg-[#738f52]/10 rounded-md"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}

                      {/* Auth Links (panel) */}
                      <div className="border-t border-[#738f52]/20 pt-4 mt-4 space-y-1">
                        {isAuthenticated ? (
                          <button
                            onClick={() => {
                              setMobileMenuOpen(false);
                              handleLogout();
                            }}
                            className="w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md"
                          >
                            Sign Out
                          </button>
                        ) : (
                          <>
                            <Link
                              to={loginHref}
                              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              Sign In
                            </Link>
                            <Link
                              to={registerHref}
                              className="block px-3 py-2 text-base font-medium text-primary-600 hover:bg-gray-50 rounded-md"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              Sign Up
                            </Link>
                          </>
                        )}
                      </div>

                      {/* Add to Home Screen */}
                      <div className="pt-2">
                        <AddToHomeScreen />
                      </div>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Transition>
      </nav>
      {/* Disclaimer Modal — rendered via Portal to escape header stacking context */}
      <Portal>
        <DisclaimerModal
          open={disclaimerOpen}
          onClose={handleDisclaimerClose}
          onAgree={handleDisclaimerAgree}
        />
      </Portal>
    </header>
  );
};

export default Header;
